import CustomDraggable from "../../parts/CustomDraggable";
import { Collapse, IconButton, makeStyles, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import CloseIcon from '@material-ui/icons/Close';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import { useRecoilState, useRecoilValue } from "recoil";
import { activeAlertTargetState } from "../../store/CopilotState";
import { coPiAlertStateById } from "../../store/CopilotListState";
import { OnClickAnchor } from "../../parts/OnClickAnchor";
import { useEffect, useRef, useState } from "react";
import { useResizeObserver } from "../../hooks/useResizeObserver";
import { timeZoneSelector } from "../../store/TimeZoneState";
import { formatTimeZoneDate } from "../../utils/FPEUtils";

const useStyles = makeStyles(() => ({
  alertWindow: {
    position: 'absolute',
    zIndex: 999,
    backgroundColor: '#FFFFFF',
    minWidth: 200,
    minHeight: 90
  },
  alertWindowHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    cursor: "move"
  },
  aircraftCode: {
    display: 'flex',
    padding: '3px 3px 3px 6px',
    fontWeight: 'bold'
  },
  closeButton: {
    cursor: 'pointer'
  },
  alertsIsNone: {
    padding: 10
  },
  iconButton: {
    padding: 0
  },
  displayNone: {
    display: 'none'
  },
  detailData: {
    paddingLeft: 24
  }
}));

const  CopilotAlertWindow = () => {
  const classes = useStyles();
  const {t} = useTranslation();
  const [targetId, setTargetId] = useRecoilState(activeAlertTargetState);
  const data = useRecoilValue(coPiAlertStateById(targetId));
  const parentContainer = document.getElementById('map-container');
  const timeZone = useRecoilValue(timeZoneSelector);

  const handleClose = () => {
    setTargetId('');
  }

  const [hasAlerts, setHasAlerts] = useState(false);
  useEffect(() => {
    if (data) {
      setHasAlerts(data.unupdated_alert_result || data.route_deviation_alert_result || data.approach_alert_result || data.cross_alert_result);
    } else {
      setHasAlerts(false);
    }
  }, [data]);

  const [base, setBase] = useState({x: 360, y: 0});
  const ref = useRef(null);
  useResizeObserver(ref, entry => {
    const baseY = parentContainer.clientHeight - entry.contentRect.height;
    setBase({x: base.x, y: baseY});
  });

  return (
    <>
      <CustomDraggable
        baseX={base.x}
        baseY={base.y}
        parent={parentContainer}
        handle=".widget-header"
      >
        <div className={clsx("widget-box", "dark-color", classes.alertWindow)} style={{bottom: '12px', left: '360px'}} hidden={targetId ? false : true} ref={ref}>
          <div className={clsx("widget-header", classes.alertWindowHeader)}>
            <h4>{t('Active Alerts')}</h4>
            <OnClickAnchor onClick={handleClose} className="close-anchor">
              <Tooltip title="Close" arrow >
                <CloseIcon/>
              </Tooltip>
            </OnClickAnchor>
          </div>
          <div className={classes.aircraftCode}>
            <div>{`${t('Aircraft Code')}: ${targetId}`}</div>
          </div>
          {
            !hasAlerts && <div className={classes.alertsIsNone}>{t('No Alerts.')}</div>
          }
          {
            (targetId && hasAlerts) && <TableContainer>
              <Table stickyHeader size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('Alert')}</TableCell>
                    <TableCell>{t('Detail')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {
                    data.unupdated_alert_result &&
                    <TableRow>
                      <TableCell>{t('Non Updated')}</TableCell>
                      <TableCell>{`${t('minutes not updated', {min: data.unupdated_time_min})}`}</TableCell>
                    </TableRow>
                  }
                  {
                    data.route_deviation_alert_result &&
                    <TableRow>
                      <TableCell>{t('deviation')}</TableCell>
                      <TableCell>{`${Math.round(data.deviation_distance_nm)}NM`}</TableCell>
                    </TableRow>
                  }
                  {
                    data.approach_alert_result &&
                      <DataRow name={t('approach')} detailName={t('approaching aircraft')} detailList={data.approach_aircraft.map(d => `${d.aircraft_code}(${Math.round(d.h_dist_nm)}NM/${Math.round(d.v_dist_ft)}ft)`)}></DataRow>
                  }
                  {
                    data.cross_alert_result &&
                      <DataRow name={t('crossing')} detailName={t('crossing aircraft')} detailList={data.cross_aircraft_code.map(d => `${d.aircraft_code}(ETD${formatTimeZoneDate(d.etd_utc, timeZone, 'HH:mmz')})`)}></DataRow>
                  }
                </TableBody>
              </Table>
            </TableContainer>
          }
        </div>
      </CustomDraggable>
    </>
  )
}

function DataRow(props) {
  const {name, detailName, detailList} = props;
  const [open, setOpen] = useState(true);
  const classes = useStyles();

  const handleExpand = () => {
    setOpen(prev => !prev);
  }

  return (
    <>
      <TableRow>
        <TableCell>{name}</TableCell>
        <TableCell>
          <IconButton onClick={handleExpand} className={classes.iconButton}>
            {open ? <KeyboardArrowDownIcon/> : <KeyboardArrowRightIcon/>}
          </IconButton>
          {detailName}
        </TableCell>
      </TableRow>
      {
        detailList.map((d, i) => {
          return (
            <TableRow className={clsx(!open && classes.displayNone)} key={`copi-alerts${i}`}>
              <TableCell></TableCell>
              <TableCell>
                <Collapse in={open} unmountOnExit>
                  <div className={classes.detailData}>{d}</div>
                </Collapse>
              </TableCell>
            </TableRow>
          )
        })
      }
    </>
  )
}

export default CopilotAlertWindow;